import React, { useState, useEffect } from 'react';
import { compressPptx } from './utils/pptxCompressor';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import TopNav from './components/TopNav';
import AdminSubbar from './components/Admin/AdminSubbar';
import AdminLayout from './components/Admin/AdminLayout';
import AdminLegacyRedirect from './components/Admin/AdminLegacyRedirect';
import { AdminOnlyRoute, PartnerAdminOnlyRoute } from './components/Admin/adminRouteGuards';
import { PARTNER_ADMIN_DOMAINS } from './components/Admin/partnerAdminNavConfig';
import BottomLeftStatusRail from './components/BottomLeftStatusRail/BottomLeftStatusRail';
import MapCombatPanel from './components/MapCombatPanel/MapCombatPanel';
import DiamondMapModal from './components/DiamondMapModal/DiamondMapModal';
import BossMapModal from './components/BossMapModal/BossMapModal';
import CramMapModal from './components/CramMapModal/CramMapModal';
import EquipmentViewportModal from './components/EquipmentViewportModal/EquipmentViewportModal';
import { MapDialogProvider, useMapDialog } from './components/MapDialog/useMapDialog';
import { MapOrbTrayLayoutProvider } from './components/MapOrbTray/MapOrbTrayLayout';
import { SkillTreeToastProvider } from './components/SkillTreeViewport/SkillTreeToastContext';
// Quiz.jsx removed — stub with no implementation, nothing links to it
import Login from './pages/Login';
import AppShell from './components/AppShell/AppShell';
// Settings is now a modal (SettingsModal) mounted in TopNav — no page route needed
// PlayerPage import removed — refactored into PlayerLayout + sub-routes (Phase 60)
import NaplexPrep from './pages/NaplexPrep';
import PtcbPrep from './pages/PtcbPrep';
import MpjePrep from './pages/MpjePrep';
import Landing from './pages/Landing';
import apiClient from './api/client';
import { initTelemetry, trackRoute } from './telemetry/uiEvents';
import './App.css';
import './components/Admin/ui/admin.tokens.css';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { TokenProvider } from './context/TokenContext';
import { XPProvider } from './context/XPContext';
import { HealthProvider } from './context/HealthContext';
import { ReplayProvider, useReplay } from './context/ReplayContext';
import { BugReportProvider } from './context/BugReportContext';
import { UISettingsProvider, useUISettings } from './context/UISettingsContext';
import { ConceptBuilderProvider, useGlobalConceptBuilder } from './context/ConceptBuilderContext';
import { HintProvider, HintLayer } from './hints';
import { ReckoningProvider } from './contexts/ReckoningContext';
import { clearPrivateSessionStorage } from './utils/authLifecycleCleanup';
import { setAnonDemoOnly } from './utils/authStorage';
import ReplayBanner from './components/ReplayBanner';
import ModelStatusBanner from './components/ModelStatusBanner';
import TutorialTestBanner from './components/TutorialTestBanner';
import { useModelStatus } from './hooks/useModelStatus';
import useLoadingTimeout from './hooks/useLoadingTimeout';
import ImportReportModal from './components/ImportReportModal';
// AdminParseModal removed (2026-04-13) — Cutover 3 (2026-05-19) further removed the accelerate toggle; uploads now use parse_mode=auto everywhere
import ErrorBoundary from './components/ErrorBoundary';
import LimitReachedModal from './components/LimitReachedModal';
import CardChangeNoticeModal from './components/CardChangeNoticeModal';
import { getCardChangeNotices } from './api/cardChanges';
import { trackDeckUpload } from './utils/analytics';
import AppLoader from './components/AppLoader/AppLoader';
import { isPublicPath } from './utils/publicPaths';

const ForgotPassword = React.lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = React.lazy(() => import('./pages/ResetPassword'));
const Upload = React.lazy(() => import('./pages/Upload'));
const BatchUpload = React.lazy(() => import('./pages/BatchUpload'));
const BatchUploadParse = React.lazy(() => import('./pages/BatchUploadParse'));
const FlashcardsLayout = React.lazy(() => import('./pages/FlashcardsLayout'));
const FlashcardsDrugs = React.lazy(() => import('./pages/FlashcardsDrugs'));
const ConceptVault = React.lazy(() => import('./pages/ConceptVault'));
const FlashcardsCalculations = React.lazy(() => import('./pages/FlashcardsCalculations'));
const Signup = React.lazy(() => import('./pages/Signup'));
const DrugCardsUpload = React.lazy(() => import('./pages/DrugCardsUpload'));
const PostExamChat = React.lazy(() => import('./pages/PostExamChat'));
const MindmapPage = React.lazy(() => import('./pages/MindmapPage'));
const ObjectiveView = React.lazy(() => import('./pages/ObjectiveView'));
const AdminSlideGaps = React.lazy(() => import('./pages/AdminSlideGaps'));
const AdminWorkspace = React.lazy(() => import('./components/Admin/AdminWorkspace'));
const BlogIndex = React.lazy(() => import('./pages/BlogIndex'));
const BlogPost = React.lazy(() => import('./pages/BlogPost'));
const Terms = React.lazy(() => import('./pages/Terms'));
const Privacy = React.lazy(() => import('./pages/Privacy'));
// Lazy, not eager like NaplexPrep: the compendium page pulls rosters.json, the 94-entry
// enemy decode, 32 card ledgers and every boss/drug glyph module. Eager would land
// all of that in the main bundle that anon landing visitors download for nothing.
const Compendium = React.lazy(() => import('./pages/Compendium/Compendium'));
const TableOfContents = React.lazy(() => import('./pages/TableOfContents'));
const LearnRouteDetail = React.lazy(() => import('./pages/LearnRouteDetail'));
const StudySession = React.lazy(() => import('./pages/StudySession'));
const SessionComplete = React.lazy(() => import('./pages/SessionComplete'));
const PastExams = React.lazy(() => import('./pages/PastExams'));
const BuilderModal = React.lazy(() => import('./components/ConceptBuilder/BuilderModal'));
const LevelSessionLazy = React.lazy(() => import('./pages/LevelSession'));
const ResuscitatePageLazy = React.lazy(() => import('./pages/ResuscitatePage'));
const DeckLevels = React.lazy(() => import('./pages/DeckLevels'));
const LevelSession = React.lazy(() => import('./pages/LevelSession'));
const FactLab = React.lazy(() => import('./pages/FactLab'));
// LectureMode merged into Upload.jsx as "Study" tab — redirects below
const BatchBossSession = React.lazy(() => import(/* webpackPrefetch: true */ './pages/BatchBossSession'));
// Dashboard was the ONLY eager heavy route — it pulls DrugLabMap -> BatchBossSession
// + the whole authenticated game tree into the main bundle (~2.75MB that even anon
// landing visitors download). Its route (path="/dashboard") already sits inside the
// app-level <Suspense>, so lazy-loading is safe and matches the other 68 lazy routes.
// NOT statically prefetched: the Dashboard tree pulls react-katex (~186KB gz via
// ConceptCard -> RichText) plus the whole game. An unconditional webpackPrefetch
// downloaded it on the ANON mobile landing — where it's never used and saturates
// the pipe the drug-lab combat chunk needs, stretching the "Loading boss
// encounter..." wait. It's now warmed programmatically in AppContent ONLY for
// real (non-anon) signed-in users who will actually open it.
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const CramSession = React.lazy(() => import('./pages/CramSession'));
const ConceptBlockDrill = React.lazy(() => import('./pages/ConceptBlockDrill'));
const TutorialTestPage = React.lazy(() => import('./pages/TutorialTest'));
const PlayerStats = React.lazy(() => import('./pages/PlayerStats'));
const PlayerShop = React.lazy(() => import('./pages/PlayerShop'));
const PlayerBattles = React.lazy(() => import('./pages/PlayerBattles'));
const DrillSession = React.lazy(() => import('./components/ConceptCard/DrillSession'));
// ⚠️ V1 REMNANT — Psychedelic Journey (Psilocybin "Neural Rewire" trial). Orphaned;
// only reachable via the /player/journey route below, not normal play. See
// compendium/reference/psychedelic-journey-v1-remnant-audit.md (V2 rebuild plan).
const JourneySession = React.lazy(() => import('./pages/JourneySession'));
const CommunityCards = React.lazy(() => import('./pages/CommunityCards'));
const SharedDrillSession = React.lazy(() => import('./components/ConceptCard/SharedDrillSession'));
const PvPQueue = React.lazy(() => import('./pages/PvPQueue'));
const PvPSession = React.lazy(() => import('./pages/PvPSession'));
const PvPLeaderboard = React.lazy(() => import('./pages/PvPLeaderboard'));
const ResuscitatePage = React.lazy(() => import('./pages/ResuscitatePage'));
const FountainSession = React.lazy(() => import('./pages/FountainSession'));
const NotesTriage = React.lazy(() => import('./pages/NotesTriage'));
const ClassHub = React.lazy(() => import('./pages/ClassHub'));
const ClassDetail = React.lazy(() => import('./pages/ClassDetail'));
const MoleculePreview = React.lazy(() => import('./pages/MoleculePreview'));
const KeystonePickPreview = React.lazy(() => import('./pages/KeystonePickPreview'));
const KeystonePreviewMount = React.lazy(() => import('./components/KeystonePickModal/KeystonePreviewMount'));
const FirstCombatGate = React.lazy(() => import('./components/FirstCombatGate/FirstCombatGate'));
const OgNaplexRender = React.lazy(() => import('./pages/OgNaplexRender'));
const OgPtcbRender = React.lazy(() => import('./pages/OgPtcbRender'));
const OgMpjeRender = React.lazy(() => import('./pages/OgMpjeRender'));

function ViewAsBanner() {
  const [username, setUsername] = React.useState(
    typeof window !== 'undefined' ? sessionStorage.getItem('view_as_username') : null
  );
  if (!username) return null;
  const exit = () => {
    sessionStorage.removeItem('view_as_token');
    sessionStorage.removeItem('view_as_username');
    setUsername(null);
    window.close();
  };
  return (
    <div
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 99999,
        background: 'linear-gradient(90deg, #fbbf24, #f59e0b)',
        color: '#0a0a0a', padding: '6px 16px',
        fontSize: 13, fontWeight: 600, textAlign: 'center',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12,
        borderBottom: '1px solid rgba(0,0,0,0.15)', letterSpacing: '0.02em',
      }}
    >
      <span>Viewing as {username} — read-only inspection. Admin session in other tabs is untouched.</span>
      <button
        onClick={exit}
        style={{
          background: '#0a0a0a', color: '#fbbf24', border: 'none',
          padding: '3px 10px', borderRadius: 4, fontWeight: 700,
          cursor: 'pointer', fontSize: 12,
        }}
      >
        Exit view-as
      </button>
    </div>
  );
}

// Admin panel token handoff: new tab opens /dashboard?_panel_token=xxx
// Must run synchronously before React mounts so AuthProvider picks up the token.
(() => {
  // Anon-demo isolation: the /play/:slug share demo opens in a NEW tab and must
  // run as a fully isolated anon session — even if the visitor is logged in
  // (a valid access_token sits in shared localStorage). Set the per-tab,
  // in-memory isolation flag SYNCHRONOUSLY here, before AuthProvider's checkAuth
  // runs, so getAuthToken() returns only the anon-demo token and the demo tab
  // presents as anon (no real identity / decks / HUD). Matches both /play and
  // /play/<slug>. Other tabs (fresh JS contexts) keep the flag false.
  // Mount-aware: under the moth hub the app is served at PUBLIC_URL=/debono, so
  // window.location.pathname is "/debono/play/..."; strip the mount before the
  // /play test (PUBLIC_URL is '' on root-served prod, so this is a no-op there).
  // Without this the anon-demo isolation never engages under the mount and the
  // demo renders the full dashboard chrome (sidebar/HUD) instead of the clean
  // anon surface -- the /debono standalone sidebar bug.
  const _mountBase = (process.env.PUBLIC_URL || '').replace(/\/+$/, '');
  const _appPath = _mountBase && window.location.pathname.startsWith(_mountBase + '/')
    ? window.location.pathname.slice(_mountBase.length)
    : window.location.pathname;
  if (/^\/play(\/|$)/.test(_appPath)) {
    setAnonDemoOnly(true);
  }

  const params = new URLSearchParams(window.location.search);
  const panelToken = params.get('_panel_token');
  if (panelToken) {
    localStorage.setItem('access_token', panelToken);
    localStorage.removeItem('active_mode');
    sessionStorage.clear();
    params.delete('_panel_token');
    const clean = params.toString();
    const url = window.location.pathname + (clean ? `?${clean}` : '');
    window.history.replaceState({}, '', url);
  }

  // View-as handoff: admin opens a NEW tab to inspect another user. Token is
  // placed in sessionStorage (tab-scoped) so the admin's localStorage auth
  // for every OTHER tab stays intact. getAuthToken() prefers sessionStorage
  // so this tab sees the target user everywhere.
  const viewAsToken = params.get('_view_as_token');
  if (viewAsToken) {
    const viewAsUser = params.get('_view_as_user') || '';
    clearPrivateSessionStorage();
    sessionStorage.setItem('view_as_token', viewAsToken);
    if (viewAsUser) sessionStorage.setItem('view_as_username', viewAsUser);
    params.delete('_view_as_token');
    params.delete('_view_as_user');
    const clean = params.toString();
    const url = window.location.pathname + (clean ? `?${clean}` : '');
    window.history.replaceState({}, '', url);
  }
})();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,        // Data fresh for 30s — prevents rapid refetches
      refetchOnWindowFocus: false, // Don't refetch all queries on tab switch
      retry: 1,                 // Single retry on failure (default 3 is excessive)
    },
  },
});

// Lazy-loaded slides page
const FlashcardsSlides = React.lazy(() => import('./pages/FlashcardsSlides'));
const AdminUiPlayground = React.lazy(() => import('./pages/AdminUiPlayground'));
const SkillTreeAdminPreview = React.lazy(() => import('./pages/admin/SkillTreeAdminPreview'));
const SkillTreeAuthoringTab = React.lazy(() => import('./pages/admin/SkillTreeAuthoringTab'));
// Asset preview renderer — used by the Mobile App (responsive-viewer) tool to
// load individual surfaces full-bleed at phone sizes. Kept after the sandbox
// nav tool was removed because responsive-viewer still links to it.
const SandboxPreviewRoute = React.lazy(() => import('./pages/admin/sandbox/SandboxPreviewRoute'));
const HARNESS_ENABLED = process.env.REACT_APP_ADMIN_HARNESS_ENABLED === 'true';
const HarnessCombatPage = HARNESS_ENABLED
  ? React.lazy(() => import('./pages/admin/lab/harness/HarnessCombatPage'))
  : null;
const HarnessSetupPage = HARNESS_ENABLED
  ? React.lazy(() => import('./pages/admin/lab/harness/HarnessSetupPage'))
  : null;
const KeystoneSynergyPreviewPage = React.lazy(() => import('./pages/admin/KeystoneSynergyPreviewPage'));
const PublicShellPreview = React.lazy(() => import('./pages/public/PublicShellPreview'));
const PlayDemo = React.lazy(() => import('./pages/PlayDemo'));

function ProtectedRoute({ children, bypassKeystoneGate = false }) {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();
  const { timedOut } = useLoadingTimeout(loading, 12000);

  if (loading) {
    if (timedOut) {
      return <Navigate to="/login" replace state={{ from: location }} />;
    }
    return <div className="suspense-spinner" />;
  }
  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  // Edward 2026-05-29: global force-keystone gate. FirstCombatGate is a
  // pass-through when settings.starter_chosen is true — so wrapping every
  // protected child with it is free for resolved users and forces the
  // KeystonePickModal for everyone else, regardless of entry route. Single
  // source of truth = users.settings (server-side lockKeystone writes it,
  // patchUser optimistically dismisses) so the modal can't loop or
  // double-mount. /dev/keystone-pick bypasses because it IS the preview.
  if (bypassKeystoneGate) {
    return children;
  }
  return (
    <React.Suspense fallback={<div className="suspense-spinner" />}>
      <FirstCombatGate>{children}</FirstCombatGate>
    </React.Suspense>
  );
}

// LectureRedirect removed — /deck/:deckId deprecated along with Upload page

/**
 * Wraps a session route in MapCombatPanel when opened from the map via the
 * background-location modal pattern. Closing the modal navigates back to the
 * background location (typically the dashboard).
 */
function SessionModalRoute() {
  const location = useLocation();
  const navigate = useNavigate();
  const bg = location.state?.backgroundLocation;
  const isCramExamDrill = /^\/learn\/cram\/exam\/[^/]+\/(?:drill|concept-drill)$/.test(location.pathname);
  const isCramConceptBlock = /^\/learn\/cram\/[^/]+\/concept-block\/[^/]+$/.test(location.pathname);
  const isCramCategory = /^\/learn\/cram\/(?!exam\/)[^/]+\/[^/]+$/.test(location.pathname);
  const isCram = isCramExamDrill || isCramConceptBlock || isCramCategory;
  const isResuscitate = location.pathname === '/learn/resuscitate';
  const isDiamondContent = /\/content(?:$|\/)/.test(location.pathname);
  // Dialysis = combat-shape reward/heal session reusing BatchBossSession's UI
  // wrapped in the standard MapCombatPanel viewport modal (same map-viewport
  // shape as drill/trial/content sessions — dashboard chrome stays mounted).
  const isDialysis = /\/dialysis(?:$|\/)/.test(location.pathname);
  const isBoss = /\/boss$/.test(location.pathname)
    || /^\/learn\/boss\/final\/[^/]+$/.test(location.pathname)
    || /^\/learn\/boss\/reckoning\/[^/]+\/final$/.test(location.pathname);
  const preloadedCombat = location.state?.preloadedCombat;
  // Per-DLC diagnostic-combat boss naming. Apothecarius (NAPLEX) is the
  // pharmacist-domain master; MPJE swaps to Praetor (Roman magistrate
  // for the law-domain register), PTCB swaps to Praeparator (the
  // preparer / compounder for the technician-domain register).
  const dlcDiagnosticName = preloadedCombat?.diagnostic_enemy?.name
    || (location.state?.isMpjeSession
      ? 'Praetor'
      : location.state?.isPtcbSession
        ? 'Caliper'
        : 'Titrant');
  // Mini-boss flow does not carry preloadedCombat — it routes via
  // miniBossEncounter.mini_boss_slug. Derive a display label from the slug
  // so BossMapModal shows the actual enemy name (e.g. "Veldra") instead of
  // falling back to the generic "Boss" placeholder.
  const miniBossEncounter = location.state?.miniBossEncounter;
  const miniBossAbilitySummary = miniBossEncounter?.ability_summary || miniBossEncounter?.abilitySummary || null;
  const miniBossSlugLabel = miniBossEncounter?.mini_boss_slug
    ? miniBossEncounter.mini_boss_slug
        .split('-')
        .map(part => part.charAt(0).toUpperCase() + part.slice(1))
        .join('-')
    : null;
  // Pool detection — drives accent overrides on BossMapModal. Slug lists
  // mirror backend/services/mini_boss_engine/pools.py.
  const ATHERO_SLUGS = new Set(['chylo', 'veldra', 'idol', 'eldra', 'lipo-alpha']);
  const ANAPHYL_SLUGS = new Set(['murin-omab', 'chimer-ximab', 'huma-zumab', 'pur-umab']);
  const miniBossPool = miniBossEncounter?.mini_boss_slug
    ? ATHERO_SLUGS.has(miniBossEncounter.mini_boss_slug)
      ? 'athero'
      : ANAPHYL_SLUGS.has(miniBossEncounter.mini_boss_slug)
        ? 'anaphyl'
        : null
    : null;
  const nodeMeta = location.state?.nodeMeta
    ? {
        ...location.state.nodeMeta,
        ability_summary: location.state.nodeMeta.ability_summary || miniBossAbilitySummary || undefined,
        pool: location.state.nodeMeta.pool || miniBossPool || undefined,
      }
    : (isBoss && preloadedCombat
      ? {
          type: preloadedCombat.boss_class === 'apothecarius' ? 'apothecarius'
            : location.state?.isGauntlet ? 'gauntlet_boss'
            : preloadedCombat.boss_class === 'final' ? 'final_boss'
            : preloadedCombat.boss_class === 'tier' ? 'tier_boss'
            : 'boss',
          label: (preloadedCombat.boss_class === 'apothecarius' || location.state?.isGauntlet)
            ? dlcDiagnosticName
            : (preloadedCombat.boss_name || miniBossSlugLabel || 'Boss'),
          short_label: preloadedCombat.boss_name || miniBossSlugLabel,
          trait: preloadedCombat.boss_trait,
          ability_summary: miniBossAbilitySummary || undefined,
          pool: miniBossPool || undefined,
        }
      : {
          type: isCram
            ? 'cram'
            : isBoss
              ? 'boss'
              : isResuscitate
                ? 'resuscitate'
                : isDialysis
                  ? 'fountain'
                  : location.pathname.match(/\/(drill|trial|content|fountain)(?:$|\/)/)?.[1] || 'topic',
          label: isBoss ? (miniBossSlugLabel || 'Boss') : undefined,
          short_label: isBoss ? miniBossSlugLabel : undefined,
          ability_summary: isBoss ? (miniBossAbilitySummary || undefined) : undefined,
          pool: isBoss ? (miniBossPool || undefined) : undefined,
        }
    );
  const close = React.useCallback(() => {
    if (bg) navigate(bg.pathname + (bg.search || ''), { replace: true, state: bg.state });
    else navigate('/dashboard', { replace: true });
  }, [bg, navigate]);

  // Diamond teaching nodes get a dedicated modal whose chrome stays out of
  // the way so DiamondSession's own purple-accent header + step indicator
  // + bottom bar can do the talking.
  if (isDiamondContent) {
    return (
      <DiamondMapModal node={nodeMeta} onClose={close}>
        <LevelSessionLazy />
      </DiamondMapModal>
    );
  }

  // Boss fights get a dedicated crimson-accent modal. BatchBossSession's
  // combat header + HP bars + question area render inside unchanged.
  if (isBoss) {
    return (
      <BossMapModal node={nodeMeta} onClose={close}>
        <React.Suspense fallback={null}>
          <FirstCombatGate>
            <BatchBossSession />
          </FirstCombatGate>
        </React.Suspense>
      </BossMapModal>
    );
  }

  if (isCram) {
    return (
      <CramMapModal node={nodeMeta} onClose={close}>
        {isCramCategory ? <CramSession /> : <ConceptBlockDrill examMode={isCramExamDrill} />}
      </CramMapModal>
    );
  }

  // Dialysis is combat-shape (BatchBossSession brings its own SessionChrome:
  // toxin HP top, question body, playerbar bottom). It needs the bare boss
  // viewport modal — BossMapModal — not MapCombatPanel. MapCombatPanel renders
  // its own .mcp-header which would stack on top of SessionChrome and produce
  // a "modal inside a modal" silhouette. BossMapModal is intentionally
  // headerless so the combat chrome reads as one continuous panel.
  // Cyan accent comes from `pool="dialysis"` → BossMapModal.css [data-pool="dialysis"].
  if (isDialysis) {
    return (
      <BossMapModal node={{ ...nodeMeta, pool: 'dialysis' }} onClose={close}>
        <React.Suspense fallback={null}>
          <FirstCombatGate>
            <BatchBossSession />
          </FirstCombatGate>
        </React.Suspense>
      </BossMapModal>
    );
  }

  return (
    <MapCombatPanel node={nodeMeta} onClose={close} onBegin={() => {}}>
      {isResuscitate
        ? <ResuscitatePageLazy embedded onExit={close} />
        : <LevelSessionLazy />}
    </MapCombatPanel>
  );
}

/**
 * Wraps a player sub-route in EquipmentViewportModal when opened from a
 * dock orb via state.backgroundLocation. Closing returns to the dashboard
 * (or whatever bg pathname the caller passed). When the user lands here
 * via direct URL with no bg, the route falls through to the regular
 * full-page render.
 */
function PlayerOrbModalRoute({ eyebrow, eyebrowMode, title, subtitle, children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const bg = location.state?.backgroundLocation;
  const close = React.useCallback(() => {
    if (bg) navigate(bg.pathname + (bg.search || ''), { replace: true, state: bg.state });
    else navigate('/dashboard', { replace: true });
  }, [bg, navigate]);
  return (
    <EquipmentViewportModal
      onClose={close}
      eyebrow={eyebrow}
      eyebrowMode={eyebrowMode}
      title={title}
      subtitle={subtitle}
    >
      {children}
    </EquipmentViewportModal>
  );
}

const SYNTHETIC_DASHBOARD_BACKGROUND = {
  pathname: '/dashboard',
  search: '',
  hash: '',
  state: {},
};

/**
 * PlayerSurfaceModalRedirect — injects a dashboard backgroundLocation
 * into history state when the user lands at a player-modal route via
 * direct URL (no in-app nav). After the redirect fires, the modal
 * Routes block matches and renders the surface as a viewport modal
 * over the dashboard. Replaces the old full-page fallbacks for
 * /player, /player/wholesaler, /bank/drugs, and
 * /bank/concepts — the modal IS the canonical render now; there is no
 * full-page mode.
 */
function PlayerSurfaceModalRedirect() {
  const location = useLocation();
  const navigate = useNavigate();
  React.useEffect(() => {
    if (location.state?.backgroundLocation) return;
    navigate(location.pathname + (location.search || ''), {
      replace: true,
      state: {
        ...(location.state || {}),
        backgroundLocation: SYNTHETIC_DASHBOARD_BACKGROUND,
        viewportOrb: true,
      },
    });
  }, [location, navigate]);
  return <div className="suspense-spinner" />;
}

function isModalSessionPath(pathname) {
  return /^\/learn\/deck\/[^/]+\/levels\/[^/]+$/.test(pathname)
    || /^\/learn\/deck\/[^/]+\/objective\/[^/]+(?:\/(?:drill|trial|content|fountain))?$/.test(pathname)
    || /^\/learn\/deck\/[^/]+\/boss$/.test(pathname)
    || /^\/learn\/boss\/final\/[^/]+$/.test(pathname)
    || /^\/learn\/cram\/exam\/[^/]+\/(?:drill|concept-drill)$/.test(pathname)
    || /^\/learn\/cram\/[^/]+\/concept-block\/[^/]+$/.test(pathname)
    || /^\/learn\/cram\/(?!exam\/)[^/]+\/[^/]+$/.test(pathname)
    || /^\/learn\/clinical-round\/[^/]+\/[^/]+$/.test(pathname)
    || /^\/learn\/(?:mpje|ptcb)\/clinical-round\/[^/]+\/[^/]+$/.test(pathname)
    || /^\/learn\/(?:naplex|mpje|ptcb)\/(?:rx-detail|safety)\/[^/]+\/[^/]+$/.test(pathname)
    || pathname === '/learn/resuscitate';
}

function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();
  // Background-location modal route pattern: when a dashboard map node opens
  // a session, we navigate to the real /learn URL with state.backgroundLocation
  // pinned to the previous page (/dashboard). The main <Routes> matches against
  // that background so the dashboard stays mounted behind the modal overlay.
  const backgroundLocation = location.state?.backgroundLocation
    || (isModalSessionPath(location.pathname) ? SYNTHETIC_DASHBOARD_BACKGROUND : null);
  const { user, loading: authLoading } = useAuth();
  const { match1440pScale, isMatch1440pScaleActive } = useUISettings();
  const { startReplay } = useReplay();
  const { showDialog } = useMapDialog();
  const conceptBuilder = useGlobalConceptBuilder();

  // Best-effort UI telemetry - install global listeners once (empty deps so the
  // capture-phase click/error/unload handlers attach a single time). apiBase is
  // sourced from the shared apiClient so the beacon hits the right backend
  // (api.debono.ai on prod, :7001 on dev) instead of the user's own machine.
  useEffect(() => {
    initTelemetry({ apiBase: apiClient.defaults.baseURL || '' });
  }, []);
  // Route-change breadcrumb - uses the already-wired location.
  useEffect(() => {
    trackRoute(location.pathname);
  }, [location.pathname]);

  const fileInputRef = React.useRef(null);
  const drugCardsInputRef = React.useRef(null);
  const notesInputRef = React.useRef(null);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [clearingData, setClearingData] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [uploadError, setUploadError] = useState(null); // string or null
  const [, setCompressProgress] = useState(null); // { stage, detail, percent }
  const [cardChangeNotices, setCardChangeNotices] = useState([]);
  const [showCardChangeNotices, setShowCardChangeNotices] = useState(false);

  // Warm the Dashboard chunk only for real signed-in users. Anon demo visitors
  // (user.is_anonymous) never reach the Dashboard, so prefetching its heavy tree
  // (react-katex ~186KB + game) on the anon landing only starves the combat
  // chunk on a slow connection. Gating it here replaces the removed static
  // webpackPrefetch on the lazy() import above.
  useEffect(() => {
    if (user && !user.is_anonymous) {
      import('./pages/Dashboard');
    }
  }, [user]);

  const handleClearAllData = async () => {
    setClearingData(true);
    try {
      const response = await apiClient.delete('/api/decks/all/clear');
      await showDialog({ title: `${response.data.message}`, accentColor: '#22c55e' });
      // Refresh the page to show empty state
      window.location.reload();
    } catch (error) {
      console.error('Failed to clear data:', error);
      await showDialog({ title: 'Failed to clear data: ' + (error.response?.data?.detail || error.message) });
    } finally {
      setClearingData(false);
      setShowClearConfirm(false);
    }
  };

  // --- Native event listener fix for hidden file input uploads ---
  // ROOT CAUSE (2026-03-11): React's synthetic onChange on hidden file inputs
  // (display:none) does NOT fire reliably in all browsers. The <input> is hidden
  // and triggered via ref.click(). In production, the native DOM 'change' event
  // fires but React's event delegation sometimes misses it. Playwright tests
  // pass because programmatic file selection bypasses this browser behavior.
  //
  // FIX: A native addEventListener('change') is attached to the file input
  // element (see useEffect below near line ~430). Both the native listener and
  // React's onChange call handleFileChange. The uploadLockRef mutex ensures only
  // one execution per file selection, even when both handlers fire.
  //
  // IF THIS REGRESSES: Check that (1) the native listener useEffect still runs
  // with no deps array (re-attaches every render), (2) uploadLockRef resets in
  // the finally block, (3) the input element has id="ppt-file-upload".
  const uploadLockRef = React.useRef(false);

  const handleFileChange = async (e) => {
    if (uploadLockRef.current) {
      console.warn('[Upload] Blocked by uploadLockRef');
      return;
    }
    const file = e.target.files?.[0];
    if (!file) {
      console.warn('[Upload] No file in change event');
      return;
    }

    // Singular method: parse_mode is always 'auto'. The hybrid routing in
    // pipeline_safety.choose_standardization_route decides cloud vs local; the
    // emergency DB kill-switch (cloud_standardization_enabled=false) is the only
    // off-ramp.
    uploadLockRef.current = true;
    try {
      await doUpload(file, 'auto');
    } finally {
      uploadLockRef.current = false;
    }
  };

  const doUpload = async (originalFile, parseMode) => {
    const sizeMB = originalFile.size / (1024 * 1024);

    if (sizeMB > 80) {
      setUploadError(`This file is ${sizeMB.toFixed(0)}MB, which is too large to upload right now (max ~80MB). Try removing large images from the PowerPoint or splitting it into smaller files.`);
      return;
    }

    const needsCompression = sizeMB >= 50;

    // For large files, navigate to dashboard and signal compression via window event.
    // setTimeout ensures Dashboard has mounted and registered its listener before the event fires.
    if (needsCompression) {
      navigate('/dashboard');
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('compress-start', { detail: { filename: originalFile.name } }));
      }, 100);
    }

    try {
      // Downscale large PPTX images in a Web Worker — keeps UI responsive.
      // Backend re-renders slides as PPTX->PDF->PNG so embedded image quality
      // beyond ~1280px doesn't affect output.
      const file = await compressPptx(originalFile, (p) => setCompressProgress(p));
      setCompressProgress(null);

      const fileMB = file.size / (1024 * 1024);
      if (fileMB > 95) {
        setUploadError(`File is still ${fileMB.toFixed(0)}MB after compression (limit ~100MB). Try a smaller file or remove large images from the PowerPoint.`);
        return;
      }

      const formData = new FormData();
      formData.append('file', file);
      formData.append('stop_at_phase', 'phase6');
      formData.append('parse_mode', parseMode);
      // Cloudflare Tunnel has ~30KB/s upload throughput, so scale timeout by file size.
      // 1MB ≈ 40s through tunnel. Add generous buffer.
      const uploadTimeoutMs = Math.max(120000, fileMB * 60000);
      // Signal Dashboard to show immediate upload feedback (FAB path has no other visual indicator)
      window.dispatchEvent(new CustomEvent('upload-start', { detail: { filename: originalFile.name } }));
      const resp = await apiClient.post('/api/decks/upload', formData, { timeout: uploadTimeoutMs });
      window.dispatchEvent(new CustomEvent('compress-end'));
      trackDeckUpload(resp.data.slide_count || 0);
      navigate('/dashboard', {
        state: {
          deckUploaded: Date.now(),
          newDeck: {
            id: resp.data.id,
            filename: file.name,
            parsing_status: 'parsing',
            parse_mode: parseMode,
            pipeline_config: resp.data.pipeline_config || { current_phase: 'phase1', phases_completed: [], progress: { total_phases: 8 } },
          },
        },
      });
      window.dispatchEvent(new CustomEvent('upload-end'));
    } catch (error) {
      window.dispatchEvent(new CustomEvent('upload-end'));
      setCompressProgress(null);
      const status = error.response?.status || 'network';
      const detail = error.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (detail?.message || error.message || 'Unknown');
      console.error('[Upload] Error:', status, error.response?.data || error.message);
      window.dispatchEvent(new CustomEvent('compress-fail', { detail: { message: msg } }));
      // Compress-needed flow already navigated to /dashboard before the request
      // (line ~403). Dashboard's MapCombatPanel handles the error UI via the
      // compress-fail event above. Firing setUploadError here would mount the
      // legacy .confirm-dialog-overlay (z-index 1000) on top of MapCombatPanel
      // (z-index 100), giving the user the wrong chrome with the right modal
      // flickering through. Only fall back to the legacy dialog for the small-
      // file path (no navigate happened, dashboard isn't mounted to receive the
      // compress-fail event).
      if (!needsCompression) {
        if (error.response?.status === 409 && error.response.data?.detail?.type === 'duplicate') {
          navigate('/dashboard');
          setUploadError('This file was already uploaded.');
        } else {
          setUploadError(`Upload failed: ${msg}`);
        }
      }
    }
  };

  // AdminParseModal removed (2026-04-13) — Cutover 3 (2026-05-19) further removed the accelerate toggle; uploads now use parse_mode=auto everywhere

  const handleDrugCardsFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Navigate to drug cards upload page with file
      navigate('/upload-drugs', { state: { file } });
    }
    // Reset input
    if (drugCardsInputRef.current) {
      drugCardsInputRef.current.value = '';
    }
  };

  const handleNotesFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (notesInputRef.current) notesInputRef.current.value = '';

    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await apiClient.post('/api/notes/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      // Navigate to triage page — will show processing status or results
      navigate(`/notes/${response.data.id}/triage`);
    } catch (err) {
      console.error('Notes upload failed:', err);
      await showDialog({ title: 'Upload failed: ' + (err.response?.data?.detail || err.message) });
    }
  };

  const handleImportReport = async (reportJson) => {
    await startReplay(reportJson);
    navigate('/dashboard');
  };

  // Check for pending card change notices (refunds, effect swaps)
  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    async function checkCardChangeNotices() {
      try {
        const data = await getCardChangeNotices();
        if (cancelled) return;
        if (data.notices && data.notices.length > 0) {
          setCardChangeNotices(data.notices);
          setShowCardChangeNotices(true);
        }
      } catch (err) {
        // Non-blocking — notices are best-effort
      }
    }

    checkCardChangeNotices();
    return () => { cancelled = true; };
  }, [user]);

  // --- Native event listener fallback for hidden file input (DO NOT REMOVE) ---
  // React's synthetic onChange on display:none inputs doesn't fire reliably in
  // all browsers. This native listener guarantees handleFileChange runs.
  // The uploadLockRef mutex in handleFileChange prevents double-execution when
  // both this listener and React's onChange fire. See root-cause comment above.
  React.useEffect(() => {
    const el = document.getElementById('ppt-file-upload');
    if (!el) return;
    const nativeHandler = (e) => handleFileChange(e);
    el.addEventListener('change', nativeHandler);
    return () => el.removeEventListener('change', nativeHandler);
  }); // intentionally no deps — re-attach on every render to capture latest handleFileChange

  useEffect(() => {
    const handleActivateWand = () => {
      if (!conceptBuilder.active) {
        conceptBuilder.setActive(true);
      }
    };

    window.addEventListener('tutorial:activate-wand', handleActivateWand);
    return () => window.removeEventListener('tutorial:activate-wand', handleActivateWand);
  }, [conceptBuilder]);

  // Normalize pathname so trailing-slash variants (e.g. /login/) match
  // the strict-equality guards below. Nginx/CDNs can add a trailing slash
  // on direct-URL loads; without this, /login/ renders with the authed
  // chrome still mounted because `pathname === '/login'` is false.
  const pathname = location.pathname === '/' ? '/' : location.pathname.replace(/\/+$/, '');

  const isAuthRoute = pathname === '/login' || pathname === '/signup' || pathname === '/forgot-password' || pathname === '/reset-password';
  const isPublicPage = isPublicPath(pathname);
  const isBoss = /\/boss$/.test(pathname) || /^\/learn\/boss\/final\/[^/]+$/.test(pathname);
  const isModalSession = isModalSessionPath(pathname);
  const isAdminRoute = location.pathname.startsWith('/admin') || location.pathname.startsWith('/partner-admin');

  const hideBottomStack = isAuthRoute || isPublicPage || isAdminRoute;
  const shouldScaleWorkspace = isMatch1440pScaleActive && !isAuthRoute && !isPublicPage && !isAdminRoute;
  const workspaceScaleStyle = React.useMemo(() => {
    if (!shouldScaleWorkspace) return undefined;
    return {
      '--debono-ui-scale': String(match1440pScale),
      zoom: match1440pScale,
      width: `calc(100% / ${match1440pScale})`,
      minHeight: `calc(100vh / ${match1440pScale})`,
    };
  }, [shouldScaleWorkspace, match1440pScale]);

  if (authLoading && !isPublicPage && !isAuthRoute) {
    return (
      <div className="App">
        <div className="app-loading-shell app-loader-shell">
          <AppLoader label="Loading workspace..." size="md" />
        </div>
      </div>
    );
  }

  return (
    <div className={`App${isAdminRoute ? ' admin-shell-context' : ''}`}>
      <ViewAsBanner />
      {!isAuthRoute && !isPublicPage && !isBoss && !isModalSession && <TopNav hidden />}
      {!isAuthRoute && !isPublicPage && <AdminSubbar />}
      <React.Suspense fallback={null}><KeystonePreviewMount /></React.Suspense>
      <input
        id="ppt-file-upload"
        ref={fileInputRef}
        type="file"
        accept=".pptx,.ppt,.pdf"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />
      <input
        ref={drugCardsInputRef}
        type="file"
        accept=".xlsx,.xls,.csv"
        onChange={handleDrugCardsFileChange}
        style={{ display: 'none' }}
      />
      <input
        ref={notesInputRef}
        type="file"
        accept=".pdf,.docx,.doc,.txt,.pptx,.ppt,.png,.jpg,.jpeg,.heic,.webp"
        onChange={handleNotesFileChange}
        style={{ display: 'none' }}
      />

      <ImportReportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImport={handleImportReport}
      />

      <LimitReachedModal />

      {showCardChangeNotices && cardChangeNotices.length > 0 && (
        <CardChangeNoticeModal
          notices={cardChangeNotices}
          onDone={() => {
            setShowCardChangeNotices(false);
            setCardChangeNotices([]);
          }}
        />
      )}

      <div
        className={`app-workspace-shell${shouldScaleWorkspace ? ' app-workspace-shell--scaled' : ''}`}
        style={workspaceScaleStyle}
      >
        <React.Suspense fallback={<div className="suspense-spinner" />}>
        <Routes location={backgroundLocation || location}>
          <Route path="/og-render/naplex" element={<React.Suspense fallback={null}><OgNaplexRender /></React.Suspense>} />
          <Route path="/og-render/ptcb"   element={<React.Suspense fallback={null}><OgPtcbRender /></React.Suspense>} />
          <Route path="/og-render/mpje"   element={<React.Suspense fallback={null}><OgMpjeRender /></React.Suspense>} />
          <Route path="/preview/public-shell" element={<React.Suspense fallback={<div className="suspense-spinner" />}><PublicShellPreview /></React.Suspense>} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/forgot-password" element={<React.Suspense fallback={<div className="suspense-spinner" />}><ForgotPassword /></React.Suspense>} />
          <Route path="/reset-password" element={<React.Suspense fallback={<div className="suspense-spinner" />}><ResetPassword /></React.Suspense>} />
          <Route path="/tutorial-test" element={<React.Suspense fallback={<div className="suspense-spinner" />}><TutorialTestPage /></React.Suspense>} />
          <Route path="/dashboard" element={<ProtectedRoute bypassKeystoneGate><ErrorBoundary><Dashboard /></ErrorBoundary></ProtectedRoute>} />
          <Route path="/contents" element={<ProtectedRoute><TableOfContents /></ProtectedRoute>} />
          <Route path="/past-exams" element={<ProtectedRoute><PastExams /></ProtectedRoute>} />
          {/* Upload page deprecated 2026-02-25 — see docs/deprecated-upload-page.md */}
          <Route path="/upload" element={<Navigate to="/dashboard" replace />} />
          <Route path="/deck/:deckId" element={<ProtectedRoute><ErrorBoundary><Upload /></ErrorBoundary></ProtectedRoute>} />
          <Route path="/upload-drugs" element={<ProtectedRoute><DrugCardsUpload /></ProtectedRoute>} />
          <Route path="/batch-upload" element={<ProtectedRoute><BatchUpload /></ProtectedRoute>} />
          <Route path="/batch-upload-parse" element={<ProtectedRoute><BatchUploadParse /></ProtectedRoute>} />
          <Route path="/dev/molecule" element={<ProtectedRoute><React.Suspense fallback={<div className="suspense-spinner" />}><MoleculePreview /></React.Suspense></ProtectedRoute>} />
          <Route path="/dev/keystone-pick" element={<ProtectedRoute bypassKeystoneGate><React.Suspense fallback={<div className="suspense-spinner" />}><KeystonePickPreview /></React.Suspense></ProtectedRoute>} />
          <Route
            path="/admin/lab/sandbox/preview/:assetId"
            element={(
              <ProtectedRoute>
                <AdminOnlyRoute>
                  <React.Suspense fallback={<div className="suspense-spinner" />}>
                    <SandboxPreviewRoute />
                  </React.Suspense>
                </AdminOnlyRoute>
              </ProtectedRoute>
            )}
          />
          {HARNESS_ENABLED && (
            <>
              <Route
                path="/admin/lab/combat-harness"
                element={(
                  <ProtectedRoute>
                    <AdminOnlyRoute>
                      <React.Suspense fallback={<div className="suspense-spinner" />}>
                        <HarnessSetupPage />
                      </React.Suspense>
                    </AdminOnlyRoute>
                  </ProtectedRoute>
                )}
              />
              <Route
                path="/admin/lab/combat-harness/session/:sessionId"
                element={(
                  <ProtectedRoute>
                    <AdminOnlyRoute>
                      <React.Suspense fallback={<div className="suspense-spinner" />}>
                        <HarnessCombatPage />
                      </React.Suspense>
                    </AdminOnlyRoute>
                  </ProtectedRoute>
                )}
              />
            </>
          )}
          {/* Admin routes — consolidated into five domain shells with legacy redirects */}
          <Route path="/admin" element={<ProtectedRoute><AdminOnlyRoute><AdminLayout /></AdminOnlyRoute></ProtectedRoute>}>
            <Route index element={<Navigate to="review?tool=inbox" replace />} />
            <Route path="review" element={<AdminWorkspace domainKey="review" />} />
            <Route path="audit" element={<AdminWorkspace domainKey="audit" />} />
            <Route path="forge" element={<AdminWorkspace domainKey="forge" />} />
            <Route path="lab" element={<AdminWorkspace domainKey="lab" />} />
            <Route path="ops" element={<AdminWorkspace domainKey="ops" />} />
            <Route path="keystone-synergies" element={<React.Suspense fallback={<div className="suspense-spinner" />}><KeystoneSynergyPreviewPage /></React.Suspense>} />
            <Route path="ui-playground" element={<React.Suspense fallback={<div className="suspense-spinner" />}><AdminUiPlayground /></React.Suspense>} />
            <Route path="skill-tree" element={<React.Suspense fallback={<div className="suspense-spinner" />}><SkillTreeAdminPreview /></React.Suspense>} />
            <Route path="skill-tree-authoring" element={<React.Suspense fallback={<div className="suspense-spinner" />}><SkillTreeAuthoringTab /></React.Suspense>} />
            <Route path="slide-gaps" element={<AdminSlideGaps />} />
            <Route path="*" element={<AdminLegacyRedirect />} />
          </Route>
          <Route path="/partner-admin" element={<ProtectedRoute><PartnerAdminOnlyRoute><AdminLayout /></PartnerAdminOnlyRoute></ProtectedRoute>}>
            <Route index element={<Navigate to="review?tool=inbox" replace />} />
            <Route path="review" element={<AdminWorkspace domainKey="review" navConfig={PARTNER_ADMIN_DOMAINS} basePath="/partner-admin" />} />
            <Route path="audit" element={<AdminWorkspace domainKey="audit" navConfig={PARTNER_ADMIN_DOMAINS} basePath="/partner-admin" />} />
            <Route path="forge" element={<AdminWorkspace domainKey="forge" navConfig={PARTNER_ADMIN_DOMAINS} basePath="/partner-admin" />} />
            <Route path="lab" element={<AdminWorkspace domainKey="lab" navConfig={PARTNER_ADMIN_DOMAINS} basePath="/partner-admin" />} />
            <Route path="ops" element={<AdminWorkspace domainKey="ops" navConfig={PARTNER_ADMIN_DOMAINS} basePath="/partner-admin" />} />
            <Route path="*" element={<Navigate to="review?tool=inbox" replace />} />
          </Route>
          <Route element={<ProtectedRoute><AppShell /></ProtectedRoute>}>
            <Route path="/bank" element={<FlashcardsLayout />}>
              <Route index element={<Navigate to="/bank/drugs" replace />} />
              <Route path="calculations" element={<FlashcardsCalculations />} />
              <Route path="slides" element={<React.Suspense fallback={<div className="suspense-spinner" />}><FlashcardsSlides /></React.Suspense>} />
              {/* Backward-compat redirects for old content type routes */}
              <Route path="physiology" element={<Navigate to="/bank/slides?type=physiology" replace />} />
              <Route path="physiology/*" element={<Navigate to="/bank/slides?type=physiology" replace />} />
              <Route path="pathophysiology" element={<Navigate to="/bank/slides?type=pathophysiology" replace />} />
              <Route path="pathophysiology/*" element={<Navigate to="/bank/slides?type=pathophysiology" replace />} />
              <Route path="drug-information" element={<Navigate to="/bank/slides?type=drug-information" replace />} />
              <Route path="drug-information/*" element={<Navigate to="/bank/slides?type=drug-information" replace />} />
              <Route path="etiology" element={<Navigate to="/bank/slides?type=etiology" replace />} />
              <Route path="etiology/*" element={<Navigate to="/bank/slides?type=etiology" replace />} />
              <Route path="diagnosis" element={<Navigate to="/bank/slides?type=diagnosis" replace />} />
              <Route path="diagnosis/*" element={<Navigate to="/bank/slides?type=diagnosis" replace />} />
              <Route path="treatment" element={<Navigate to="/bank/slides?type=treatment" replace />} />
              <Route path="treatment/*" element={<Navigate to="/bank/slides?type=treatment" replace />} />
              <Route path="unknown" element={<Navigate to="/bank/slides" replace />} />
              <Route path="unknown/*" element={<Navigate to="/bank/slides" replace />} />
            </Route>
            <Route path="/concept-vault" element={<Navigate to="/bank/concepts" replace />} />
            <Route path="/bank/drugs" element={<PlayerSurfaceModalRedirect />} />
            <Route path="/bank/concepts" element={<PlayerSurfaceModalRedirect />} />
            <Route path="/deck/:deckId/mindmap" element={<ErrorBoundary><MindmapPage /></ErrorBoundary>} />
            <Route path="/objective/:deckId/:objectiveIndex" element={<ErrorBoundary><ObjectiveView /></ErrorBoundary>} />
            <Route path="/ask" element={<ErrorBoundary><PostExamChat /></ErrorBoundary>} />
            <Route path="/player" element={<PlayerSurfaceModalRedirect />} />
            <Route path="/player/wholesaler" element={<PlayerSurfaceModalRedirect />} />
            <Route path="/player/battles" element={<ErrorBoundary><React.Suspense fallback={<div className="suspense-spinner" />}><PlayerBattles /></React.Suspense></ErrorBoundary>} />
          </Route>
          <Route path="/bank/concepts/:cardId" element={<ProtectedRoute><Navigate to="/bank/concepts" replace /></ProtectedRoute>} />
          <Route path="/bank/concepts/:cardId/drill" element={
            <ProtectedRoute>
              <React.Suspense fallback={<div className="loading-spinner" />}>
                <DrillSession />
              </React.Suspense>
            </ProtectedRoute>
          } />
          <Route path="/concepts/browse" element={
            <ProtectedRoute>
              <React.Suspense fallback={<div className="loading-spinner" />}>
                <CommunityCards />
              </React.Suspense>
            </ProtectedRoute>
          } />
          <Route path="/concepts/shared/:cardId/drill" element={
            <ProtectedRoute>
              <React.Suspense fallback={<div className="loading-spinner" />}>
                <SharedDrillSession />
              </React.Suspense>
            </ProtectedRoute>
          } />
          <Route path="/lecture" element={<Navigate to="/dashboard" replace />} />
          <Route path="/lecture/:deckId" element={<Navigate to="/dashboard" replace />} />
          <Route path="/learn" element={<Navigate to="/dashboard" replace />} />
          <Route path="/learn/:routeId" element={<ProtectedRoute><LearnRouteDetail /></ProtectedRoute>} />
          <Route path="/learn/:routeId/session" element={<ProtectedRoute><StudySession /></ProtectedRoute>} />
          <Route path="/learn/:routeId/session/complete" element={<ProtectedRoute><SessionComplete /></ProtectedRoute>} />
          <Route path="/learn/deck/:deckId/levels" element={<ProtectedRoute><React.Suspense fallback={<div className="suspense-spinner" />}><DeckLevels /></React.Suspense></ProtectedRoute>} />
          <Route path="/learn/deck/:deckId/levels/:subtopicId" element={<ProtectedRoute><React.Suspense fallback={<div className="suspense-spinner" />}><LevelSession /></React.Suspense></ProtectedRoute>} />
          <Route path="/learn/deck/:deckId/objective/:objectiveIdx" element={<ProtectedRoute><React.Suspense fallback={<div className="suspense-spinner" />}><LevelSession /></React.Suspense></ProtectedRoute>} />
          <Route path="/learn/deck/:deckId/objective/:objectiveIdx/drill" element={<ProtectedRoute><React.Suspense fallback={<div className="suspense-spinner" />}><LevelSession /></React.Suspense></ProtectedRoute>} />
          <Route path="/learn/deck/:deckId/objective/:objectiveIdx/trial" element={<ProtectedRoute><React.Suspense fallback={<div className="suspense-spinner" />}><LevelSession /></React.Suspense></ProtectedRoute>} />
          <Route path="/learn/deck/:deckId/objective/:objectiveIdx/content" element={<ProtectedRoute><React.Suspense fallback={<div className="suspense-spinner" />}><LevelSession /></React.Suspense></ProtectedRoute>} />
          <Route path="/learn/deck/:deckId/objective/:objectiveIdx/fountain" element={<ProtectedRoute><React.Suspense fallback={<div className="suspense-spinner" />}><FountainSession /></React.Suspense></ProtectedRoute>} />
          <Route path="/learn/deck/:deckId/objective/:objectiveIdx/dialysis" element={<ProtectedRoute><ErrorBoundary><React.Suspense fallback={<div className="suspense-spinner" />}><FirstCombatGate><BatchBossSession /></FirstCombatGate></React.Suspense></ErrorBoundary></ProtectedRoute>} />
          <Route path="/learn/deck/:deckId/boss" element={<ProtectedRoute><ErrorBoundary><React.Suspense fallback={<div className="suspense-spinner" />}><FirstCombatGate><BatchBossSession /></FirstCombatGate></React.Suspense></ErrorBoundary></ProtectedRoute>} />
          <Route path="/learn/boss/final/:examId" element={<ProtectedRoute><ErrorBoundary><React.Suspense fallback={<div className="suspense-spinner" />}><FirstCombatGate><BatchBossSession /></FirstCombatGate></React.Suspense></ErrorBoundary></ProtectedRoute>} />
          <Route path="/learn/boss/reckoning/:reckoningId/final" element={<ProtectedRoute><ErrorBoundary><React.Suspense fallback={<div className="suspense-spinner" />}><FirstCombatGate><BatchBossSession /></FirstCombatGate></React.Suspense></ErrorBoundary></ProtectedRoute>} />
          <Route path="/learn/cram/exam/:examId/drill" element={<ProtectedRoute><React.Suspense fallback={<div className="suspense-spinner" />}><ConceptBlockDrill examMode /></React.Suspense></ProtectedRoute>} />
          <Route path="/learn/cram/:deckId/:category" element={<ProtectedRoute><React.Suspense fallback={<div className="suspense-spinner" />}><CramSession /></React.Suspense></ProtectedRoute>} />
          <Route path="/learn/cram/exam/:examId/concept-drill" element={<ProtectedRoute><React.Suspense fallback={<div className="suspense-spinner" />}><ConceptBlockDrill examMode /></React.Suspense></ProtectedRoute>} />
          <Route path="/learn/cram/:deckId/concept-block/:planetSlug" element={<ProtectedRoute><React.Suspense fallback={<div className="suspense-spinner" />}><ConceptBlockDrill /></React.Suspense></ProtectedRoute>} />
          <Route path="/learn/clinical-round/:areaKey/:roundKey" element={<ProtectedRoute><React.Suspense fallback={<div className="suspense-spinner" />}><LevelSession /></React.Suspense></ProtectedRoute>} />
          <Route path="/learn/naplex/rx-detail/:areaKey/:slug" element={<ProtectedRoute><React.Suspense fallback={<div className="suspense-spinner" />}><LevelSession /></React.Suspense></ProtectedRoute>} />
          <Route path="/learn/naplex/safety/:areaKey/:groupKey" element={<ProtectedRoute><React.Suspense fallback={<div className="suspense-spinner" />}><LevelSession /></React.Suspense></ProtectedRoute>} />
          <Route path="/learn/mpje/clinical-round/:areaKey/:roundKey" element={<ProtectedRoute><React.Suspense fallback={<div className="suspense-spinner" />}><LevelSession /></React.Suspense></ProtectedRoute>} />
          <Route path="/learn/mpje/rx-detail/:areaKey/:slug" element={<ProtectedRoute><React.Suspense fallback={<div className="suspense-spinner" />}><LevelSession /></React.Suspense></ProtectedRoute>} />
          <Route path="/learn/mpje/safety/:areaKey/:groupKey" element={<ProtectedRoute><React.Suspense fallback={<div className="suspense-spinner" />}><LevelSession /></React.Suspense></ProtectedRoute>} />
          <Route path="/learn/ptcb/clinical-round/:areaKey/:roundKey" element={<ProtectedRoute><React.Suspense fallback={<div className="suspense-spinner" />}><LevelSession /></React.Suspense></ProtectedRoute>} />
          <Route path="/learn/ptcb/rx-detail/:areaKey/:slug" element={<ProtectedRoute><React.Suspense fallback={<div className="suspense-spinner" />}><LevelSession /></React.Suspense></ProtectedRoute>} />
          <Route path="/learn/ptcb/safety/:areaKey/:groupKey" element={<ProtectedRoute><React.Suspense fallback={<div className="suspense-spinner" />}><LevelSession /></React.Suspense></ProtectedRoute>} />
          <Route path="/learn/resuscitate" element={<ProtectedRoute><React.Suspense fallback={<div className="suspense-spinner" />}><ResuscitatePage /></React.Suspense></ProtectedRoute>} />
          <Route path="/notes/:notesId/triage" element={<ProtectedRoute><React.Suspense fallback={<div className="suspense-spinner" />}><NotesTriage /></React.Suspense></ProtectedRoute>} />
          <Route path="/learn/fact-lab/:deckId" element={<ProtectedRoute><React.Suspense fallback={<div className="suspense-spinner" />}><FactLab /></React.Suspense></ProtectedRoute>} />
          <Route path="/train" element={<Navigate to="/" replace />} />
          <Route path="/practice" element={<Navigate to="/" replace />} />
          <Route path="/chat" element={<Navigate to="/ask" replace />} />
          {/* Settings is now a modal in TopNav — redirect old URL to dashboard */}
          <Route path="/settings" element={<Navigate to="/dashboard" replace />} />
          {/* ⚠️ V1 REMNANT route — Psychedelic Journey (Psilocybin "Neural Rewire" trial).
              Orphaned: no in-app link points here; the Neural Rewire activation that
              should redirect users returns `journey_required` with no FE consumer.
              Preserved for a V2 rebuild — see compendium/reference/psychedelic-journey-v1-remnant-audit.md */}
          <Route path="/player/journey" element={<ProtectedRoute><React.Suspense fallback={<div className="suspense-spinner" />}><JourneySession /></React.Suspense></ProtectedRoute>} />
          <Route path="/player/shop" element={<Navigate to="/player/wholesaler" replace />} />
          <Route path="/social" element={<ProtectedRoute><ErrorBoundary><React.Suspense fallback={<div className="suspense-spinner" />}><PvPQueue /></React.Suspense></ErrorBoundary></ProtectedRoute>} />
          <Route path="/social/match/:matchId" element={<ProtectedRoute><ErrorBoundary><React.Suspense fallback={<div className="suspense-spinner" />}><PvPSession /></React.Suspense></ErrorBoundary></ProtectedRoute>} />
          <Route path="/social/leaderboard" element={<ProtectedRoute><React.Suspense fallback={<div className="suspense-spinner" />}><PvPLeaderboard /></React.Suspense></ProtectedRoute>} />
          <Route path="/pvp" element={<Navigate to="/social" replace />} />
          <Route path="/pvp/match/:matchId" element={<Navigate to="/social" replace />} />
          <Route path="/pvp/leaderboard" element={<Navigate to="/social/leaderboard" replace />} />
          <Route path="/class" element={<ProtectedRoute><React.Suspense fallback={<div className="suspense-spinner" />}><ClassHub /></React.Suspense></ProtectedRoute>} />
          <Route path="/class/:classId" element={<ProtectedRoute><React.Suspense fallback={<div className="suspense-spinner" />}><ClassDetail /></React.Suspense></ProtectedRoute>} />
          <Route path="/naplex-prep" element={<NaplexPrep />} />
          <Route path="/naplex" element={<NaplexPrep />} />
          <Route path="/ptcb-prep" element={<PtcbPrep />} />
          <Route path="/ptcb" element={<PtcbPrep />} />
          <Route path="/mpje-prep" element={<MpjePrep />} />
          <Route path="/mpje" element={<MpjePrep />} />
          {/* Hypertension shareable demo — no-login public play link (registered in
              utils/publicPaths.js so the auth gate doesn't redirect to /login). */}
          <Route path="/play/finerenone" element={<Navigate to="/play/hypertension" replace />} />
          <Route path="/play/:slug" element={<React.Suspense fallback={<div className="suspense-spinner" />}><PlayDemo /></React.Suspense>} />
          <Route path="/play" element={<Navigate to="/play/hypertension" replace />} />
          <Route path="/blog" element={<BlogIndex />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          {/* Debono Compendium — public, zero-network reference for every enemy, drug and
              status tooltip. Registered in utils/publicPaths.js so the auth gate does
              not bounce a logged-out visitor to /login. The splat is the degrade path:
              there is no app-level catch-all here, so an unmatched /compendium/* would render
              a blank viewport with no chrome and no route back. */}
          <Route path="/compendium" element={<Compendium />} />
          <Route path="/compendium/*" element={<Navigate to="/compendium" replace />} />
          {/* /pricing was stripped 2026-05-02 - direct-to-Stripe via upgradeFlow.
              Redirect stale bookmarks to dashboard. */}
          <Route path="/pricing" element={<Navigate to="/dashboard" replace />} />
          <Route path="/pricing/*" element={<Navigate to="/dashboard" replace />} />
          <Route path="/" element={<Landing />} />
        </Routes>
        {/* Modal Routes — render when backgroundLocation is present. The map
            viewport modal hosts session routes without navigating away from
            the dashboard. */}
        {backgroundLocation && (
          <Routes>
            <Route path="/learn/deck/:deckId/levels/:subtopicId" element={<ProtectedRoute><ErrorBoundary><SessionModalRoute /></ErrorBoundary></ProtectedRoute>} />
            <Route path="/learn/deck/:deckId/objective/:objectiveIdx" element={<ProtectedRoute><ErrorBoundary><SessionModalRoute /></ErrorBoundary></ProtectedRoute>} />
            <Route path="/learn/deck/:deckId/objective/:objectiveIdx/drill" element={<ProtectedRoute><ErrorBoundary><SessionModalRoute /></ErrorBoundary></ProtectedRoute>} />
            <Route path="/learn/deck/:deckId/objective/:objectiveIdx/trial" element={<ProtectedRoute><ErrorBoundary><SessionModalRoute /></ErrorBoundary></ProtectedRoute>} />
            <Route path="/learn/deck/:deckId/objective/:objectiveIdx/content" element={<ProtectedRoute><ErrorBoundary><SessionModalRoute /></ErrorBoundary></ProtectedRoute>} />
            <Route path="/learn/deck/:deckId/objective/:objectiveIdx/fountain" element={<ProtectedRoute><ErrorBoundary><SessionModalRoute /></ErrorBoundary></ProtectedRoute>} />
            <Route path="/learn/deck/:deckId/objective/:objectiveIdx/dialysis" element={<ProtectedRoute><ErrorBoundary><SessionModalRoute /></ErrorBoundary></ProtectedRoute>} />
            <Route path="/learn/deck/:deckId/boss" element={<ProtectedRoute><ErrorBoundary><SessionModalRoute /></ErrorBoundary></ProtectedRoute>} />
            <Route path="/learn/boss/final/:examId" element={<ProtectedRoute><ErrorBoundary><SessionModalRoute /></ErrorBoundary></ProtectedRoute>} />
            <Route path="/learn/boss/reckoning/:reckoningId/final" element={<ProtectedRoute><ErrorBoundary><SessionModalRoute /></ErrorBoundary></ProtectedRoute>} />
            <Route path="/learn/cram/exam/:examId/drill" element={<ProtectedRoute><ErrorBoundary><SessionModalRoute /></ErrorBoundary></ProtectedRoute>} />
            <Route path="/learn/cram/:deckId/:category" element={<ProtectedRoute><ErrorBoundary><SessionModalRoute /></ErrorBoundary></ProtectedRoute>} />
            <Route path="/learn/cram/exam/:examId/concept-drill" element={<ProtectedRoute><ErrorBoundary><SessionModalRoute /></ErrorBoundary></ProtectedRoute>} />
            <Route path="/learn/cram/:deckId/concept-block/:planetSlug" element={<ProtectedRoute><ErrorBoundary><SessionModalRoute /></ErrorBoundary></ProtectedRoute>} />
            <Route path="/learn/clinical-round/:areaKey/:roundKey" element={<ProtectedRoute><ErrorBoundary><SessionModalRoute /></ErrorBoundary></ProtectedRoute>} />
            <Route path="/learn/naplex/rx-detail/:areaKey/:slug" element={<ProtectedRoute><ErrorBoundary><SessionModalRoute /></ErrorBoundary></ProtectedRoute>} />
            <Route path="/learn/naplex/safety/:areaKey/:groupKey" element={<ProtectedRoute><ErrorBoundary><SessionModalRoute /></ErrorBoundary></ProtectedRoute>} />
            <Route path="/learn/mpje/clinical-round/:areaKey/:roundKey" element={<ProtectedRoute><ErrorBoundary><SessionModalRoute /></ErrorBoundary></ProtectedRoute>} />
            <Route path="/learn/mpje/rx-detail/:areaKey/:slug" element={<ProtectedRoute><ErrorBoundary><SessionModalRoute /></ErrorBoundary></ProtectedRoute>} />
            <Route path="/learn/mpje/safety/:areaKey/:groupKey" element={<ProtectedRoute><ErrorBoundary><SessionModalRoute /></ErrorBoundary></ProtectedRoute>} />
            <Route path="/learn/ptcb/clinical-round/:areaKey/:roundKey" element={<ProtectedRoute><ErrorBoundary><SessionModalRoute /></ErrorBoundary></ProtectedRoute>} />
            <Route path="/learn/ptcb/rx-detail/:areaKey/:slug" element={<ProtectedRoute><ErrorBoundary><SessionModalRoute /></ErrorBoundary></ProtectedRoute>} />
            <Route path="/learn/ptcb/safety/:areaKey/:groupKey" element={<ProtectedRoute><ErrorBoundary><SessionModalRoute /></ErrorBoundary></ProtectedRoute>} />
            <Route path="/learn/resuscitate" element={<ProtectedRoute><ErrorBoundary><SessionModalRoute /></ErrorBoundary></ProtectedRoute>} />
            <Route path="/player/wholesaler" element={<ProtectedRoute><PlayerOrbModalRoute eyebrow="Wholesaler" title="Drug Catalog"><React.Suspense fallback={<div className="suspense-spinner" />}><PlayerShop /></React.Suspense></PlayerOrbModalRoute></ProtectedRoute>} />
            <Route path="/player" element={<ProtectedRoute><PlayerOrbModalRoute eyebrow="Player" title="Stats"><React.Suspense fallback={<div className="suspense-spinner" />}><PlayerStats /></React.Suspense></PlayerOrbModalRoute></ProtectedRoute>} />
            <Route path="/bank/drugs" element={<ProtectedRoute><PlayerOrbModalRoute eyebrow="Pharmacy" eyebrowMode="blue" title=""><FlashcardsDrugs /></PlayerOrbModalRoute></ProtectedRoute>} />
            <Route path="/bank/concepts" element={<ProtectedRoute><PlayerOrbModalRoute eyebrow="Concept Vault" eyebrowMode="blue" title=""><React.Suspense fallback={<div className="suspense-spinner" />}><ConceptVault /></React.Suspense></PlayerOrbModalRoute></ProtectedRoute>} />
          </Routes>
        )}
        </React.Suspense>
        {!hideBottomStack && <div className="bottom-left-stack" />}
        {/* Gate the rail on the SAME condition as its portal host: on public
            pages (incl. /play), auth routes, and admin routes the host
            `.bottom-left-stack` isn't rendered, so the rail returns null anyway —
            but mounting it still ran logged-in-only effects (achievements/quests/
            honor) off a direct localStorage `access_token` read, which in an
            isolated /play demo tab could 401 and trigger a GENERIC anon re-mint
            that clobbers the curated demo token. Not mounting it avoids that. */}
        {!hideBottomStack && <BottomLeftStatusRail />}
      </div>

      {/* Old SelectionPanel hidden — replaced by carousel tray mode */}
      {conceptBuilder.builderModalOpen && (
        <React.Suspense fallback={<div className="suspense-spinner" />}>
          <BuilderModal
            selections={conceptBuilder.selections}
            deckId={conceptBuilder.selections[0]?.deck_id || ''}
            onClose={() => conceptBuilder.setBuilderModalOpen(false)}
            onSaved={() => {
              conceptBuilder.setBuilderModalOpen(false);
              conceptBuilder.setActive(false);
              conceptBuilder.clearSelections();
              conceptBuilder.notifyCardSaved();
            }}
          />
        </React.Suspense>
      )}

      {/* Upload error modal — matches site's confirm-dialog styling */}
      {uploadError && (
        <div className="confirm-dialog-overlay" onClick={() => setUploadError(null)}>
          <div className="confirm-dialog" onClick={e => e.stopPropagation()}>
            <h3>Upload Error</h3>
            <p>{uploadError}</p>
            <div className="confirm-dialog-actions">
              <button
                className="confirm-dialog-btn confirm-dialog-btn-cancel"
                onClick={() => setUploadError(null)}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AdminParseModal removed (2026-04-13); accelerate toggle removed in Cutover 3 (2026-05-19) — singular parse_mode=auto */}

      {/* Compression overlay removed — progress shown inline in Dashboard processing area */}

      {/* Clear All Data Confirmation Dialog */}
      {showClearConfirm && (
        <div className="confirm-dialog-overlay">
          <div className="confirm-dialog">
            <h3>Clear All Data?</h3>
            <p>This will permanently delete all uploaded decks and files. This action cannot be undone.</p>
            <div className="confirm-dialog-actions">
              <button 
                className="confirm-dialog-btn confirm-dialog-btn-cancel"
                onClick={() => setShowClearConfirm(false)}
                disabled={clearingData}
              >
                Cancel
              </button>
              <button 
                className="confirm-dialog-btn confirm-dialog-btn-danger"
                onClick={handleClearAllData}
                disabled={clearingData}
              >
                {clearingData ? 'Clearing...' : 'Clear All Data'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <UISettingsProvider>
        <TokenProvider>
          <XPProvider>
          <HealthProvider>
          <ReplayProvider>
          <ReckoningProvider>
            {/* basename: CRA sets PUBLIC_URL when the app is served under a URL
                path prefix (e.g. /debono on moth.stokasoftware.com). Without this,
                every absolute <Link to="/..."> escapes the prefix. Empty in the
                normal root-served case, so this is a no-op there. */}
            <Router basename={process.env.PUBLIC_URL || '/'}>
              <BugReportProvider>
              <MapDialogProvider>
              <MapOrbTrayLayoutProvider>
              <HintProvider>
                <SkillTreeToastProvider>
                <ConceptBuilderProvider>
                  <ReplayBanner />
                  <TutorialTestBanner />
                  <AppContent />
                  <ModelStatusBannerWrapper />
                </ConceptBuilderProvider>
                </SkillTreeToastProvider>
                <HintLayer />
              </HintProvider>
              </MapOrbTrayLayoutProvider>
              </MapDialogProvider>
              </BugReportProvider>
            </Router>
          </ReckoningProvider>
          </ReplayProvider>
          </HealthProvider>
          </XPProvider>
        </TokenProvider>
        </UISettingsProvider>
      </AuthProvider>
    </QueryClientProvider>
    </HelmetProvider>
  );
}

function ModelStatusBannerWrapper() {
  const { isAuthenticated } = useAuth();
  const modelStatus = useModelStatus();
  
  if (!isAuthenticated) {
    return null;
  }
  
  return <ModelStatusBanner modelAvailable={modelStatus.modelAvailable} cloudFallback={modelStatus.cloudFallback} source={modelStatus.source} />;
}

export default App;
